import type { Session } from "@supabase/supabase-js";
import { getSupabase, isSupabaseConfigured } from "./supabase";

/**
 * Supabase Auth wrapper. The auth model is layered:
 *   1. Cloudflare Access at the perimeter (handled by cfAccess.ts).
 *   2. Supabase magic-link for the session JWT that drives RLS.
 *
 * Both layers must succeed for a user to reach the war room. CF Access
 * gates entry to the app; Supabase Auth gates queries against the DB.
 *
 * In local dev with no Supabase env vars, isSupabaseConfigured() returns
 * false and the app skips this layer (CF dev-shim alone). RLS-gated rows
 * won't load in that mode — that's expected.
 */

export interface MagicLinkResult {
  ok: boolean;
  error?: string;
}

export async function getSupabaseSession(): Promise<Session | null> {
  const client = getSupabase();
  if (!client) return null;
  const { data, error } = await client.auth.getSession();
  if (error) {
    // Not fatal — the user will just be sent through the magic-link flow.
    console.warn("[supabaseAuth] getSession error:", error.message);
    return null;
  }
  return data.session;
}

/**
 * Send a magic-link to the supplied email. The email will be lower-cased
 * by Supabase. After the user clicks the link, they're redirected back
 * to `window.location.origin` and detectSessionInUrl picks up the token.
 */
export async function signInWithMagicLink(
  email: string,
): Promise<MagicLinkResult> {
  const client = getSupabase();
  if (!client) {
    return { ok: false, error: "Supabase not configured" };
  }

  const { error } = await client.auth.signInWithOtp({
    email: email.trim().toLowerCase(),
    options: {
      emailRedirectTo: window.location.origin,
      // shouldCreateUser left at default (true) — first-time users get
      // an app_users row created via berserkr-app migration / seed flow.
      // For a closed v0, set Supabase Auth to disable signups in dashboard.
    },
  });

  if (error) {
    return { ok: false, error: error.message };
  }
  return { ok: true };
}

export async function signOutSupabase(): Promise<void> {
  const client = getSupabase();
  if (!client) return;
  await client.auth.signOut();
}

/**
 * Subscribe to Supabase auth state changes. Returns an unsubscribe fn.
 * If Supabase isn't configured, the returned unsubscribe is a no-op.
 */
export function onSupabaseAuthChange(
  callback: (session: Session | null) => void,
): () => void {
  const client = getSupabase();
  if (!client) return () => undefined;

  const { data } = client.auth.onAuthStateChange((_event, session) => {
    callback(session);
  });

  return () => data.subscription.unsubscribe();
}

export { isSupabaseConfigured };
